import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Vote, Users, Clock } from 'lucide-react';
function AnimatedCounter({
  target,
  duration = 2
}: {
  target: number;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = target / (duration * 60); // Assuming 60fps
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, target, duration]);
  return <span ref={ref} data-id="element-48">{count}</span>;
}
export function Coverage() {
  return <section id="elections" className="py-28 px-4 sm:px-6 lg:px-8 bg-zinc-900/30 border-y border-zinc-800/50 relative" data-id="element-49">
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-5 mix-blend-overlay" data-id="element-50"></div>

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-51">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-20" data-id="element-52">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 text-zinc-100" data-id="element-53">
            Election Coverage Experience
          </h2>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-54">
            Extensive on-ground and strategic experience in covering major
            democratic exercises, shaping political narratives, and managing
            media operations during critical electoral periods across{' '}
            <strong className="text-zinc-200 font-medium" data-id="element-55">
              Uttar Pradesh and India
            </strong>
            .
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-56">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative group overflow-hidden rounded-2xl bg-zinc-950 border border-zinc-800 p-10 text-center hover:border-amber-500/50 transition-colors duration-500 shadow-2xl" data-id="element-57">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-58"></div>
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-amber-700 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-500" data-id="element-59"></div>
            <div className="relative z-10 flex flex-col items-center" data-id="element-60">
              <div className="w-20 h-20 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500" data-id="element-61">
                <Vote className="w-10 h-10 text-amber-500" data-id="element-62" />
              </div>
              <div className="text-7xl font-serif font-bold text-zinc-100 mb-4 drop-shadow-md" data-id="element-63">
                <AnimatedCounter target={4} data-id="element-64" />
              </div>
              <h3 className="text-lg font-medium text-amber-500/90 uppercase tracking-widest" data-id="element-65">
                Parliamentary
              </h3>
              <p className="text-zinc-500 mt-2 text-sm uppercase tracking-wider" data-id="element-66">
                Elections
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative group overflow-hidden rounded-2xl bg-zinc-950 border border-zinc-800 p-10 text-center hover:border-amber-500/50 transition-colors duration-500 shadow-2xl" data-id="element-67">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-68"></div>
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-amber-700 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-500" data-id="element-69"></div>
            <div className="relative z-10 flex flex-col items-center" data-id="element-70">
              <div className="w-20 h-20 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500" data-id="element-71">
                <Users className="w-10 h-10 text-amber-500" data-id="element-72" />
              </div>
              <div className="text-7xl font-serif font-bold text-zinc-100 mb-4 drop-shadow-md" data-id="element-73">
                <AnimatedCounter target={6} data-id="element-74" />
              </div>
              <h3 className="text-lg font-medium text-amber-500/90 uppercase tracking-widest" data-id="element-75">
                Assembly
              </h3>
              <p className="text-zinc-500 mt-2 text-sm uppercase tracking-wider" data-id="element-76">
                Elections
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative group overflow-hidden rounded-2xl bg-zinc-950 border border-zinc-800 p-10 text-center hover:border-amber-500/50 transition-colors duration-500 shadow-2xl" data-id="element-77">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-78"></div>
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-amber-700 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-500" data-id="element-79"></div>
            <div className="relative z-10 flex flex-col items-center" data-id="element-80">
              <div className="w-20 h-20 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-500" data-id="element-81">
                <Clock className="w-10 h-10 text-amber-500" data-id="element-82" />
              </div>
              <div className="text-7xl font-serif font-bold text-zinc-100 mb-4 drop-shadow-md flex items-center justify-center" data-id="element-83">
                <AnimatedCounter target={28} data-id="element-84" />
                <span className="text-5xl text-amber-500 ml-1" data-id="element-85">+</span>
              </div>
              <h3 className="text-lg font-medium text-amber-500/90 uppercase tracking-widest" data-id="element-86">
                Years Combined
              </h3>
              <p className="text-zinc-500 mt-2 text-sm uppercase tracking-wider" data-id="element-87">
                Experience
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}